import React, { useRef, useState } from 'react'
import { Alert, Badge, Card, Typography, Spin, Button, Modal, Input, message, Radio } from 'antd'
import { WhatsAppOutlined, PhoneOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiUrl } from '../config/api'
import { SERVICE_FIELD_CONFIG, SERVICE_COLORS, SERVICE_EMOJI } from '../config/serviceFields'
import OtpAuthFlow from '../components/OtpAuthFlow.jsx'

export default function Categories() {
    const navigate = useNavigate()
    const raw = localStorage.getItem('deliveryLocation')
    let deliveryId = null
    if (raw) {
        try {
            const obj = JSON.parse(raw)
            if (obj && typeof obj === 'object') {
                deliveryId = obj.id ?? null
            }
        } catch {
            // ignore parse errors; keep deliveryId null
        }
    }

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['shopsTags', deliveryId],
        enabled: !!deliveryId,
        queryFn: async () => {
            const url = apiUrl(`/api/main-categories?deliveryLocationId=${encodeURIComponent(deliveryId)}`)
            const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
            if (!res.ok) {
                throw new Error(`فشل في جلب التصنيفات (${res.status})`)
            }
            return res.json()
        }
    })

    const { data: servicesData = [], isLoading: servicesLoading } = useQuery({
        queryKey: ['services', deliveryId],
        queryFn: async () => {
            const params = deliveryId ? `?deliveryLocationId=${encodeURIComponent(deliveryId)}` : ''
            const res = await fetch(apiUrl(`/api/services${params}`), { headers: { 'Accept': 'application/json' } })
            if (!res.ok) throw new Error('Failed to fetch services')
            return res.json()
        }
    })

    const items = Array.isArray(data) ? data : []
    const services = React.useMemo(() => {
        const arr = Array.isArray(servicesData) ? [...servicesData] : []
        return arr.sort((a, b) => {
            const aDisabled = !!a.disabledReason
            const bDisabled = !!b.disabledReason
            if (aDisabled === bDisabled) return 0
            return aDisabled ? 1 : -1
        })
    }, [servicesData])

    const comingSoon = [
        { name: 'دجاج ولحوم', imgUrl: 'https://images.leen-go.com/categories/la7meh.jpg' },
        { name: 'أدوات منزلية', imgUrl: 'https://images.leen-go.com/categories/adwat.png' },
        { name: 'مكتبة وقرطاسيّة', imgUrl: 'https://images.leen-go.com/categories/qertaseya.jpg' },
        { name: 'أسماك', imgUrl: 'https://images.leen-go.com/categories/denees.jpg' },
        { name: 'محمص', imgUrl: 'https://images.leen-go.com/categories/mksrat.jpg' },
        { name: 'صيدلية', imgUrl: 'https://images.leen-go.com/categories/pharm.png' },
        { name: 'قسم الورد', imgUrl: 'https://images.leen-go.com/categories/wrd.jpg' },
        { name: 'هدايا وعطور', imgUrl: 'https://images.leen-go.com/categories/otoor.jpg' },
        { name: 'قسم الطّفل', imgUrl: 'https://images.leen-go.com/categories/atfal.jpg' },
        { name: 'أدوات كهربائية', imgUrl: 'https://images.leen-go.com/categories/kahraba2eyi.jpg' },
        { name: 'بالونات وزينة', imgUrl: 'https://images.leen-go.com/categories/zeneh.jpg' },
        { name: 'ألعاب أطفال', imgUrl: 'https://images.leen-go.com/categories/babyToys.jpg' },
        { name: 'عسل', imgUrl: 'https://images.leen-go.com/categories/asal.jpg' },
    ]

    const [serviceModalOpen, setServiceModalOpen] = useState(false)
    const [serviceSuccessModalOpen, setServiceSuccessModalOpen] = useState(false)
    const [selectedService, setSelectedService] = useState(null)
    const [serviceFieldValues, setServiceFieldValues] = useState({})
    const [serviceNotes, setServiceNotes] = useState('')
    const [serviceSubmitting, setServiceSubmitting] = useState(false)
    const otpRef = useRef(null)
    const pendingServiceRef = useRef(null)

    function getAccessToken() {
        try { return localStorage.getItem('accessToken') || '' } catch { return '' }
    }

    function getFieldConfig(serviceKey) {
        return SERVICE_FIELD_CONFIG[serviceKey] || null
    }

    function getInitialFieldValues(serviceKey) {
        const config = getFieldConfig(serviceKey)
        if (config?.selector?.defaultValue != null) {
            return { [config.selector.key]: config.selector.defaultValue }
        }
        return {}
    }

    function isConditionalConfig(config) {
        return config && config.selector && config.fieldSets
    }

    function getFieldsToShow(serviceKey) {
        const config = getFieldConfig(serviceKey)
        if (!config) return []
        if (isConditionalConfig(config)) {
            const selVal = serviceFieldValues[config.selector.key]
            return (config.fieldSets[selVal] || [])
        }
        return config.fields || []
    }

    function openServiceConfirmation(service) {
        const token = getAccessToken()
        if (!token) {
            pendingServiceRef.current = service
            otpRef.current?.open()
            return
        }
        setSelectedService(service)
        setServiceFieldValues(getInitialFieldValues(service.serviceKey))
        setServiceNotes('')
        setServiceModalOpen(true)
    }

    function onOtpSuccess() {
        const service = pendingServiceRef.current
        pendingServiceRef.current = null
        if (service) {
            setSelectedService(service)
            setServiceFieldValues(getInitialFieldValues(service.serviceKey))
            setServiceNotes('')
            setServiceModalOpen(true)
        }
    }

    function setFieldValue(key, value) {
        setServiceFieldValues(prev => {
            const next = { ...prev, [key]: value }
            const config = selectedService ? getFieldConfig(selectedService.serviceKey) : null
            if (config && isConditionalConfig(config) && key === config.selector.key) {
                const otherFields = Object.keys(config.fieldSets).flatMap(k => config.fieldSets[k].map(f => f.key))
                otherFields.forEach(k => { if (k !== key) next[k] = '' })
            }
            return next
        })
    }

    function buildNotesPayload() {
        const parts = []
        const config = selectedService ? getFieldConfig(selectedService.serviceKey) : []
        if (isConditionalConfig(config)) {
            const selVal = serviceFieldValues[config.selector.key]
            if (selVal) parts.push(`${config.selector.label}: ${config.selector.options.find(o => o.value === selVal)?.label || selVal}`)
        }
        const fields = selectedService ? getFieldsToShow(selectedService.serviceKey) : []
        for (const f of fields) {
            const v = (serviceFieldValues[f.key] || '').trim()
            if (v) parts.push(`${f.label}: ${v}`)
        }
        const notesTrimmed = serviceNotes.trim()
        if (notesTrimmed) parts.push(notesTrimmed)
        return parts.join('\n')
    }

    function validateAndSubmit() {
        if (!selectedService) return
        const config = getFieldConfig(selectedService.serviceKey)
        if (isConditionalConfig(config)) {
            const selVal = (serviceFieldValues[config.selector.key] || '').trim()
            if (!selVal) {
                message.error(`الرجاء اختيار ${config.selector.label}`)
                return
            }
        }
        const fields = getFieldsToShow(selectedService.serviceKey)
        for (const f of fields) {
            if (f.required) {
                const v = (serviceFieldValues[f.key] || '').trim()
                if (!v) {
                    message.error(`الرجاء إدخال ${f.label}`)
                    return
                }
            }
        }
        const notesPayload = buildNotesPayload()
        if (!notesPayload.trim()) {
            message.error('الرجاء إدخال الملاحظات')
            return
        }
        submitServiceRequest(notesPayload)
    }

    async function submitServiceRequest(notesPayload) {
        if (!selectedService) return
        try {
            setServiceSubmitting(true)
            const token = getAccessToken()
            const res = await fetch(apiUrl('/api/service-requests'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    serviceName: selectedService.nameAr,
                    notes: notesPayload,
                }),
            })
            if (res.ok) {
                setServiceModalOpen(false)
                setServiceSuccessModalOpen(true)
            } else if (res.status === 401) {
                message.error('يرجى تسجيل الدخول أولاً')
                setServiceModalOpen(false)
                pendingServiceRef.current = selectedService
                otpRef.current?.open()
            } else {
                const txt = await res.text()
                message.error(txt || 'تعذر إرسال الطلب، حاول مرة أخرى')
            }
        } catch {
            message.error('حدث خطأ، حاول مرة أخرى')
        } finally {
            setServiceSubmitting(false)
        }
    }

    function renderServiceIcon(service) {
        const emoji = service?.serviceKey ? SERVICE_EMOJI[service.serviceKey] : null
        return <span style={{ fontSize: 32 }}>{emoji || '🛎️'}</span>
    }

    return (
        <div style={{ display: 'grid', gap: 'clamp(12px, 3vw, 20px)' }}>

            {/* Missing Item Contact Row */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Card bodyStyle={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <Typography.Text strong style={{ fontSize: 'clamp(14px, 4vw, 16px)' }}>
                            ناقصك اشي ومش لاقيه؟ اعتبره عندك! تواصل معنا عالواتس اب ←
                        </Typography.Text>
                        <Button
                            type="primary"
                            style={{ backgroundColor: '#25D366', borderColor: '#25D366', display: 'flex', alignItems: 'center', gap: 5 }}
                            icon={<WhatsAppOutlined />}
                            onClick={() => {
                                const phone = '970595566789';
                                const text = encodeURIComponent('مرحباً! في اشي ناقصني...');
                                window.open(`https://wa.me/${phone}?text=${text}`, '_blank', 'noopener,noreferrer');
                            }}
                        >
                            تواصل معنا
                        </Button>
                    </div>
                </Card>
            </div>

            {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                    <Spin />
                </div>
            )}

            {isError && (
                <Card>
                    <Typography.Text type="danger" style={{ marginBottom: 12, display: 'block' }}>
                        حدث خطأ أثناء جلب التصنيفات: {error?.message || 'خطأ غير معروف'}
                    </Typography.Text>
                    <Button onClick={() => refetch()}>إعادة المحاولة</Button>
                </Card>
            )}

            {!isLoading && !isError && items.length === 0 && (
                <Card>
                    <Typography.Paragraph style={{ margin: 0 }}>
                        جميع المحلات مغلقة أو غير متاحة حاليا
                    </Typography.Paragraph>
                </Card>
            )}

            {!isLoading && !isError && items.length > 0 && (
                <div
                    style={{
                        display: 'grid',
                        gap: 'clamp(12px, 3vw, 20px)',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))'
                    }}
                >
                    {items.map(cat => {
                        const isClosed = cat?.isOpened === false
                        const showIds = typeof localStorage !== 'undefined' && localStorage.getItem('showIds') === 'true'
                        const card = (
                            <Card
                                style={{
                                    padding: 10,
                                    border: "1px solid #cccccc",
                                    boxShadow: "3px 2px 3px #cccccc",
                                    cursor: isClosed ? 'default' : 'pointer',
                                    filter: isClosed ? 'grayscale(100%)' : undefined,
                                    opacity: isClosed ? 0.7 : 1,
                                    height: '100%',
                                }}
                                hoverable={!isClosed}
                                onClick={() => {
                                if (isClosed) return
                                if (cat.displayMode === 'UNIFIED_CATALOG') {
                                    navigate(`/catalog/${cat.id}`)
                                } else {
                                    navigate(`/shops/${cat.id}`)
                                }
                            }}
                                cover={
                                    <div style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', background: '#fafafa' }}>
                                        <img
                                            src={cat.imageUrl ? `https://images.leen-go.com/${cat.imageUrl}` : '/no-image.png'}
                                            alt={cat.tagAr || cat.tag || 'تصنيف'}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            loading="lazy"
                                            onError={(e) => { if (e.currentTarget.src.endsWith('/no-image.png')) return; e.currentTarget.src = '/no-image.png'; }}
                                        />
                                    </div>
                                }
                            >
                                <div style={{ textAlign: 'center' }}>
                                    {showIds && cat?.id != null && (
                                        <span style={{ display: 'inline-block', fontSize: 16, fontWeight: 'bold', fontFamily: 'monospace', background: '#f0f0f0', color: '#666', padding: '2px 6px', borderRadius: 4, marginBottom: 4 }}>
                                            #{cat.id}
                                        </span>
                                    )}
                                </div>
                                <Typography.Paragraph
                                    title={cat.tagAr || cat.tag}
                                    style={{ marginBottom: 0, fontSize: 20, textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    {cat.tagAr || cat.tag}
                                </Typography.Paragraph>
                            </Card>
                        )
                        return isClosed ? (
                            <Badge.Ribbon text="مغلق" color="red" key={cat.id}>
                                {card}
                            </Badge.Ribbon>
                        ) : (
                            <React.Fragment key={cat.id}>{card}</React.Fragment>
                        )
                    })}

                    {comingSoon.map((item, idx) => (
                        <Badge.Ribbon text="قريباً ..." color="orange" key={`cs-${idx}`}>
                            <Card
                                style={{
                                    padding: 10,
                                    border: "1px solid #cccccc",
                                    boxShadow: "3px 2px 3px #cccccc",
                                    cursor: 'default',
                                    filter: 'grayscale(100%)',
                                    opacity: 0.8,
                                    height: '100%',
                                }}
                                cover={
                                    <div style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', background: '#fafafa' }}>
                                        <img
                                            src={item.imgUrl}
                                            alt={item.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            onError={(e) => { if (e.currentTarget.src.endsWith('/no-image.png')) return; e.currentTarget.src = '/no-image.png'; }}
                                        />
                                    </div>
                                }
                            >
                                <Typography.Paragraph
                                    style={{ marginBottom: 0, fontSize: 20, textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    {item.name}
                                </Typography.Paragraph>
                            </Card>
                        </Badge.Ribbon>
                    ))}
                </div>
            )}

            {/* Services Section */}
            <div style={{ marginTop: 32, paddingBottom: 24 }}>
                <Typography.Title level={4} style={{ marginBottom: 12, textAlign: 'center' }}>
                    🛎️ خدمات
                </Typography.Title>
                <Typography.Paragraph style={{ textAlign: 'center', color: '#888', marginBottom: 16 }}>
                    اضغط على الخدمة المطلوبة وسنتواصل معك هاتفياً لتأكيد الطلب
                </Typography.Paragraph>
                {servicesLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spin /></div>
                ) : (
                    <div
                        style={{
                            display: 'grid',
                            gap: 'clamp(12px, 3vw, 16px)',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                        }}
                    >
                        {services.map(svc => {
                            const disabledReason = svc.disabledReason
                            const color = SERVICE_COLORS[svc.serviceKey] || '#fafafa'
                            const card = (
                                <Card
                                    key={svc.id}
                                    hoverable={!disabledReason}
                                    onClick={() => !disabledReason && openServiceConfirmation(svc)}
                                    style={{
                                        border: '1px solid #e8e8e8',
                                        borderRadius: 12,
                                        textAlign: 'center',
                                        background: color,
                                        cursor: disabledReason ? 'default' : 'pointer',
                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                        filter: disabledReason ? 'grayscale(100%)' : undefined,
                                        opacity: disabledReason ? 0.8 : 1,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                    bodyStyle={{
                                        padding: '20px 12px',
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                    onMouseEnter={e => { if (!disabledReason) { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)' } }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
                                >
                                    <div style={{ marginBottom: 8 }}>{renderServiceIcon(svc)}</div>
                                    <Typography.Text strong style={{ fontSize: 16 }}>
                                        {svc.nameAr}
                                    </Typography.Text>
                                </Card>
                            )
                            return disabledReason ? (
                                <Badge.Ribbon text={disabledReason} color="orange" key={svc.id} style={{ zIndex: 10 }} rootStyle={{ height: '100%' }}>
                                    {card}
                                </Badge.Ribbon>
                            ) : (
                                <div key={svc.id} style={{ height: '100%' }}>{card}</div>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* Service Modal */}
            <Modal
                title={selectedService ? selectedService.nameAr : 'طلب خدمة'}
                open={serviceModalOpen}
                onCancel={() => !serviceSubmitting && setServiceModalOpen(false)}
                footer={[
                    <Button key="cancel" disabled={serviceSubmitting} onClick={() => setServiceModalOpen(false)}>
                        إلغاء
                    </Button>,
                    <Button key="submit" type="primary" loading={serviceSubmitting} onClick={validateAndSubmit}>
                        تأكيد الطلب
                    </Button>,
                ]}
            >
                <div style={{ display: 'grid', gap: 16, textAlign: 'center' }}>
                    {selectedService && (
                        <div style={{
                            background: SERVICE_COLORS[selectedService.serviceKey] || '#fafafa',
                            borderRadius: 12,
                            padding: 20,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 8,
                        }}>
                            {renderServiceIcon(selectedService)}
                            <Typography.Title level={4} style={{ marginBottom: 0 }}>
                                {selectedService.nameAr}
                            </Typography.Title>
                        </div>
                    )}
                    {selectedService?.serviceKey && (() => {
                        const config = getFieldConfig(selectedService.serviceKey)
                        const alert = config?.alert
                        return alert ? <Alert message={alert} type="info" showIcon /> : null
                    })()}
                    <div style={{
                        background: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: 8,
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}>
                        <PhoneOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                        <Typography.Text style={{ fontSize: 15 }}>
                            عند إكمال طلبك سنقوم بالتّواصل معك لتأكيد الطّلب
                        </Typography.Text>
                    </div>
                    {selectedService && (() => {
                        const config = getFieldConfig(selectedService.serviceKey)
                        if (isConditionalConfig(config)) {
                            return (
                                <div key="conditional" style={{ display: 'grid', gap: 16 }}>
                                    <div style={{ textAlign: 'right' }}>
                                        <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                                            {config.selector.label} <span style={{ color: 'red' }}>*</span>
                                        </label>
                                        <Radio.Group
                                            value={serviceFieldValues[config.selector.key] || ''}
                                            onChange={e => setFieldValue(config.selector.key, e.target.value)}
                                            options={config.selector.options.map(o => ({ label: o.label, value: o.value }))}
                                            style={{ display: 'flex', gap: 16 }}
                                        />
                                    </div>
                                    {getFieldsToShow(selectedService.serviceKey).map(f => (
                                        <div key={f.key} style={{ textAlign: 'right' }}>
                                            <label style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>
                                                {f.label} {f.required && <span style={{ color: 'red' }}>*</span>}
                                            </label>
                                            <Input
                                                placeholder={f.placeholder}
                                                value={serviceFieldValues[f.key] || ''}
                                                onChange={e => setFieldValue(f.key, e.target.value)}
                                                maxLength={256}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )
                        }
                        return (config?.fields || []).map(f => (
                            <div key={f.key} style={{ textAlign: 'right' }}>
                                <label style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>
                                    {f.label} {f.required && <span style={{ color: 'red' }}>*</span>}
                                </label>
                                <Input
                                    placeholder={f.placeholder}
                                    value={serviceFieldValues[f.key] || ''}
                                    onChange={e => setFieldValue(f.key, e.target.value)}
                                    maxLength={256}
                                />
                            </div>
                        ))
                    })()}
                    <div style={{ textAlign: 'right' }}>
                        <label style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>
                            ملاحظات 
                        </label>
                        <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            placeholder={getFieldsToShow(selectedService?.serviceKey)?.length ? 'ملاحظات إضافية...' : ''}
                            value={serviceNotes}
                            onChange={e => setServiceNotes(e.target.value)}
                        />
                    </div>
                </div>
            </Modal>

            {/* Service success confirmation modal */}
            <Modal
                title="تم استلام طلبك"
                open={serviceSuccessModalOpen}
                centered
                onCancel={() => setServiceSuccessModalOpen(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setServiceSuccessModalOpen(false)}>
                        إغلاق
                    </Button>,
                ]}
            >
                <Typography.Paragraph style={{ margin: 0, textAlign: 'center', fontSize: 16 }}>
                    تم استلام طلب الخدمة بنجاح. سنتواصل معك في أقرب وقت ممكن.
                </Typography.Paragraph>
            </Modal>

            <OtpAuthFlow ref={otpRef} onSuccess={onOtpSuccess} />
        </div>
    )
}
